/**
 * The caller's most recent conversations, for the palette's Chats scope before
 * anything is typed. The same `GET /api/sessions` page the chat panel's
 * "Previous conversations" menu reads, newest first, so it is per-user by
 * construction and adds no storage.
 */

import { useQuery } from '@tanstack/react-query';
import { listAgentSessionPage } from '../../services/agentChat.service';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';

/** Workspace is in the key so switching workspace never shows the last one's chats. */
export const recentChatsQueryKey = (workspaceId: string) =>
  ['recent-chats', { workspaceId }] as const;

export const RECENT_CHATS_LIMIT = 8;

export function useRecentChats({ enabled }: { readonly enabled: boolean }) {
  const workspaceId = useWorkspaceId();
  const result = useQuery({
    queryKey: recentChatsQueryKey(workspaceId || ''),
    queryFn: () => listAgentSessionPage(0, RECENT_CHATS_LIMIT),
    enabled: !!workspaceId && enabled,
    // 0 overrides the app-wide 30 s default (lib/queryClient.ts): cached rows
    // still paint at once, and each opening refetches, so a chat started,
    // renamed or deleted in the panel never lingers.
    staleTime: 0,
  });
  return {
    sessions: result.data?.sessions ?? [],
    // A failed list must not read as "you have no chats".
    failed: result.data?.failed === true || result.isError,
    isLoading: result.isLoading,
  };
}
